import React, { useState } from "react";
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  OutlinedInput,
} from "@material-ui/core";

import { Labeled, useInput, useTranslate } from "react-admin";

import { AddIcon } from "./iconography";

/** Custom input component used by OutlinedInput.
 *  Forwards ref to the REAL <input> so MUI can manage focus correctly.
 *  Spreads all OutlinedInput-provided props onto that input.
 */
const ChipInputComponent = React.forwardRef(function ChipInputComponent(
  props,
  ref
) {
  const {
    elements,
    removeElement,
    availableTokens,
    currentText,
    setCurrentText,
    commitText,
    placeholder,
    ...inputProps
  } = props;

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      sx={{ padding: "8px" }}
    >
      {elements.map((el, i) =>
        el.type === "text" ? (
          <Chip
            key={i}
            label={el.value}
            onDelete={() => removeElement(i)}
            color="default"
            size="small"
            style={{ marginRight: "3px", borderRadius: "8px" }}
          />
        ) : (
          <Chip
            key={i}
            color="primary"
            label={
              availableTokens.find((t) => t.key === el.value)?.label || el.value
            }
            onDelete={() => removeElement(i)}
            size="small"
            style={{ marginRight: "3px", borderRadius: "8px" }}
          />
        )
      )}

      <input
        ref={ref}
        {...inputProps}
        placeholder={placeholder}
        style={{
          border: "none",
          outline: "none",
          flex: "none", // 👈 keeps caret after chips
          width: "auto",
          minWidth: 60,
          fontSize: "0.85rem",
          padding: "4px",
        }}
        value={currentText}
        onChange={(e) => setCurrentText(e.target.value)}
        onBlur={(e) => {
          commitText();
          if (inputProps.onBlur) inputProps.onBlur(e);
        }}
      />
    </Box>
  );
});

export const TokenInput = ({
  source,
  availableTokens = [],
  label,
  prefix,
  helperText,
  margin = "dense",
  loading = false,
  validate,
  ...rest
}) => {
  const {
    input: { value, onChange, onBlur: raOnBlur },
    meta: { touched, error, submitError },
    isRequired,
    id,
  } = useInput({
    source,
    validate,
    // format & parse to bridge DB <-> UI
    format: (v) => {
      if (!v) return [];
      if (Array.isArray(v)) return v;
      try {
        return JSON.parse(v); // DB string → array
      } catch {
        return [];
      }
    },
    parse: (v) => {
      if (!v) return null;
      try {
        return JSON.stringify(v); // array → DB string
      } catch {
        return null;
      }
    },
    ...rest,
  });
  const translate = useTranslate();
  const safeValue = Array.isArray(value) ? value : [];
  const [elements, setElements] = useState(safeValue);
  const [currentText, setCurrentText] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [focused, setFocused] = useState(false);

  const isLastModifierValue = (key) => /^\{\s*@last\d+\s*\}$/i.test(key);
  const isLastModifierElement = (el) =>
    el?.type === "token" && isLastModifierValue(el.value);
  const lastElementIsLast = isLastModifierElement(
    elements[elements.length - 1]
  );

  const commitText = () => {
    if (currentText.trim() !== "") {
      const newEls = [...elements, { type: "text", value: currentText.trim() }];
      setElements(newEls);
      onChange(newEls);
      setCurrentText("");
    }
  };

  const handleInsertToken = (token) => {
    commitText();
    // Disallow consecutive {@lastN} modifier tokens
    if (lastElementIsLast && isLastModifierValue(token.key)) {
      setMenuAnchor(null);
      return;
    }
    const newEls = [...elements, { type: "token", value: token.key }];
    setElements(newEls);
    onChange(newEls);
    setMenuAnchor(null);
  };

  const removeElement = (index) => {
    const newEls = elements.filter((_, i) => i !== index);
    setElements(newEls);
    onChange(newEls);
    if (typeof raOnBlur === "function") raOnBlur();
  };

  if (loading) {
    return (
      <Labeled
        id={id}
        label={label}
        source={source}
        isRequired={isRequired}
        meta={{ touched, error, submitError }}
        margin={margin}
      >
        <LinearProgress />
      </Labeled>
    );
  }

  const hasValue = Boolean(currentText) || elements.length > 0;
  const showNotch = focused || hasValue || prefix;

  return (
    <FormControl
      variant="outlined"
      margin={margin}
      fullWidth
      error={touched && !!(error || submitError)}
    >
      <InputLabel htmlFor={id} shrink={showNotch} required={isRequired}>
        {label}
      </InputLabel>
      <OutlinedInput
        id={id}
        name={source}
        label={isRequired ? `${label} *` : label}
        notched={showNotch}
        fullWidth
        inputComponent={ChipInputComponent}
        inputProps={{
          elements,
          removeElement,
          availableTokens,
          currentText,
          setCurrentText,
          commitText,
          onFocus: () => setFocused(true),
          onBlur: (e) => {
            setFocused(false);
            if (typeof raOnBlur === "function") raOnBlur(e);
          },
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              commitText();
            }
          },
          placeholder: "Type here...",
        }}
        startAdornment={
          prefix ? (
            <InputAdornment position="start">{prefix}</InputAdornment>
          ) : null
        }
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="insert variable"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              edge="end"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        }
      />

      {(touched && (error || submitError)) || helperText ? (
        <FormHelperText>
          {error || submitError
            ? translate(error || submitError, { _: error || submitError })
            : helperText}
        </FormHelperText>
      ) : null}

      <Menu
        anchorEl={menuAnchor}
        keepMounted
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {availableTokens
          .filter(
            (token) => !(lastElementIsLast && isLastModifierValue(token.key))
          )
          .map((token) => (
            <MenuItem key={token.key} onClick={() => handleInsertToken(token)}>
              {token.label}
            </MenuItem>
          ))}
      </Menu>
    </FormControl>
  );
};
